import * as React from "react";
import { cn } from "@/lib/utils";

interface GradientTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  /** Use animated accent gradient for brand emphasis */
  accent?: boolean;
  /** Custom gradient classes */
  gradient?: string;
  /** HTML element to render */
  as?: "span" | "h1" | "h2" | "h3" | "h4" | "p";
}

/**
 * GradientText (Neo-Brutalist Version)
 *
 * Renders text with solid colors by default (Epilogue font, near-black).
 * Use `accent` prop for colorful gradient effect.
 */
export function GradientText({
  children,
  accent = false,
  gradient,
  as: Component = "span",
  className,
  ...props
}: GradientTextProps) {
  return (
    <Component
      className={cn(
        "font-['Epilogue'] font-bold",
        accent ? "headline-gradient" : "text-[#1A1A1A]",
        gradient,
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

type BadgeColor = "yellow" | "pink" | "blue" | "green";

interface SectionLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Background color for the badge */
  color?: BadgeColor;
}

const badgeColors: Record<BadgeColor, string> = {
  yellow: "bg-[#FFDC00]",
  pink: "bg-[#FF73A9]",
  blue: "bg-[#3F5EF4] text-white",
  green: "bg-[#00EB90]",
};

/**
 * SectionLabel (Neo-Brutalist Version)
 *
 * Bold uppercase badge with black border and hard shadow.
 * Used for section categorization.
 */
export function SectionLabel({
  children,
  className,
  color = "yellow",
  ...props
}: SectionLabelProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2",
        "px-4 py-2 rounded-full",
        "border-3 border-black",
        "shadow-[3px_3px_0_0_#000000]",
        "text-xs font-bold tracking-widest uppercase",
        "font-['Sora']",
        badgeColors[color],
        // Default text color is black unless overridden by badgeColors
        !color.includes("blue") && "text-black",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}
